'use client';
/**
 * components/AppShell.tsx — OGRYN
 *
 * Pont entre le layout serveur (app/layout.tsx) et le shell applicatif.
 * Plus aucune bifurcation desktop/mobile : le shell desktop (Navbar/Footer/
 * jour-nuit/connexion/« Panneau Pro ») a été supprimé du code le 21/09 —
 * l'app mobile couvre déjà tout, cette chrome n'avait plus d'utilité et
 * polluait chaque test sur ordinateur. Desktop et mobile rendent désormais
 * le même MobileShell ; l'usage desktop reste possible pour l'instant
 * (nécessaire aux tests), il sera bloqué plus tard.
 *
 * ⚠️  Aucun import Leaflet ici.
 */

import { type ReactNode, useState, useSyncExternalStore } from 'react';
import { usePathname } from 'next/navigation';
import { MobileProvider } from '@/contexts/MobileContext';
import MobileShell from '@/components/MobileShell';
import { BootFrame } from '@/components/BootScreen';
import { LumiereVivante } from '@/components/lumiere-vivante';

interface AppShellProps {
  children: ReactNode;
  /** Palier hérité d'un rechargement dur précédent, lu côté serveur (cookie
   *  `ogryn_boot_pct`, voir lib/bootPct.ts + app/layout.tsx) : le SSR rend
   *  directement le bon pourcentage, sans correction après coup — une
   *  correction client-side (sessionStorage puis useLayoutEffect) a été
   *  tentée deux fois le 30/07 et ne peut rien contre un premier paint SSR
   *  déjà rendu à 6 %, valeur par défaut. */
  initialPct?: number;
}

export default function AppShell({ children, initialPct = 6 }: AppShellProps) {
  const pathname = usePathname();
  const [pct] = useState(initialPct);

  // Verrou de montage : tant que le client n'a pas hydraté, on ne rend que
  // cet écran neutre. `mounted` démarre à false des deux côtés (SSR + 1er
  // rendu client) → aucun hydration mismatch.
  const mounted = useSyncExternalStore(
    () => () => {},     // pas de source externe à écouter, juste server↔client
    () => true,          // snapshot client : monté
    () => false,         // snapshot serveur : pas encore
  );

  // L'atelier est une maquette plein écran : ni navigation, ni pied de page, ni
  // voile de connexion par-dessus la carte. Sur un appareil pris pour un poste de
  // travail (iPad en paysage, navigateur d'ordinateur), le shell venait
  // s'empiler sur l'application et masquait ses propres commandes.
  if (pathname?.startsWith('/lab')) return <>{children}</>;

  if (!mounted) {
    return (
      <BootFrame pct={pct} label="Démarrage" />
    );
  }

  return (
    <MobileProvider>
      <LumiereVivante />
      <MobileShell>{children}</MobileShell>
    </MobileProvider>
  );
}
